'use client';

import { usePathname } from 'next/navigation';
import { trackAffiliateClick } from '@/lib/affiliates/trackClick';
import { proxiedAffiliateHref } from '@/lib/affiliates/proxy';
import { buildAffiliateSubId } from '@/lib/affiliates/metadata';

/**
 * Обёртка над <a> для всех партнёрских (outbound) ссылок.
 *
 * Делает три вещи, которых не делал голый <a target="_blank">:
 *  1) шлёт единое событие affiliate_click в аналитику (provider/vertical/country/position/price);
 *  2) проставляет rel="nofollow noopener noreferrer sponsored" (требование Google к аффилиатным
 *     ссылкам). nofollow здесь не только про SEO: без него краулеры обходят каждую партнёрскую
 *     ссылку, а они все идут через /api/r — это была наша топ-1 статья расхода функций (см. аудит
 *     2026-07: 15k инвокаций/сутки, 75% всех вызовов);
 *  3) заворачивает ссылку в /api/r, если route её пропустит (proxiedAffiliateHref): до 16.09
 *     компонент этого не делал, и 477 ссылок на /tours и /airport шли голыми — без
 *     first-party редиректа их режет адблок (аудит 2026-09-16, §4).
 *
 * Пропсы трекинга (provider/vertical/country/position/price) не попадают в DOM.
 */
export default function AffiliateLink({
    href,
    provider,
    vertical,
    country,
    position,
    price,
    label,
    children,
    className = '',
    onClick,
    rel = 'nofollow noopener noreferrer sponsored',
    target = '_blank',
    ...props
}) {
    // Метка «язык.страница.позиция» для сетей: без неё доход не привязать к странице.
    const pathname = usePathname() || '';
    const [, language, ...rest] = pathname.split('/');
    const subId = buildAffiliateSubId({ language, pageSlug: rest.join('-') || 'home', position });

    const handleClick = (e) => {
        trackAffiliateClick({
            provider,
            vertical,
            country,
            position,
            price,
            label: label || (typeof children === 'string' ? children : undefined),
            href,
        });
        onClick?.(e);
    };

    return (
        <a href={proxiedAffiliateHref(href, { provider, subId })} target={target} rel={rel} className={className} onClick={handleClick} {...props}>
            {children}
        </a>
    );
}
